import { useId } from 'react';
import { Box } from '@mui/material';

import {
  tierBadgeClassNames,
  tierBadgeColors,
  tierBadgeStyles,
  type TierBadgeVariant,
} from './TierBadge.styles';
import { TIER_BADGE_PROGRESS_ARIA_LABEL } from './TierBadge.constants';
import shieldIconUrl from './Images/shield.svg';

export type { TierBadgeVariant };

const TIER_BADGE_SHIELD_HREF = `${shieldIconUrl}#academy-tier-shield`;

interface TierBadgeCommonProps {
  size?: number;
  ariaLabel?: string;
}

export type TierBadgeProps = TierBadgeCommonProps & (
  | {
      variant: Exclude<TierBadgeVariant, 'unavailable'>;
      progress?: number;
    }
  | {
      variant: 'unavailable';
      progress?: never;
    }
);

export function TierBadge({
  variant,
  size = 36,
  ariaLabel,
  progress,
}: TierBadgeProps) {
  const colors = tierBadgeColors[variant];
  const gradientId = useStableSvgId('tier-badge-fill');
  const clipPathId = useStableSvgId('tier-badge-clip');
  const progressClipPathId = useStableSvgId('tier-badge-progress-clip');
  const normalizedProgress = colors.gradient ? normalizeProgress(progress) : undefined;
  const isProgressIndicator = normalizedProgress !== undefined;
  const progressWidth = isProgressIndicator ? (normalizedProgress / 100) * 360 : 360;
  const fill = colors.gradient ? `url(#${gradientId})` : colors.fill;
  const accessibleLabel = isProgressIndicator
    ? ariaLabel || TIER_BADGE_PROGRESS_ARIA_LABEL
    : ariaLabel;
  const shine = colors.gradient ? (
    <>
      <path
        d="M54 74 C102 68 142 48 180 18 C218 48 258 68 306 74 L314 92 C260 88 220 72 180 46 C140 72 100 88 46 92 Z"
        fill="#FFFFFF"
        opacity="0.42"
      />
      <path
        d="M94 348 L262 96 L307 110 L129 375 Z"
        fill="#FFFFFF"
        opacity="0.2"
      />
      <path
        d="M221 346 C265 299 293 238 302 166 C281 268 233 346 180 389 C145 360 112 317 91 260 C105 328 137 380 180 415 C195 403 209 389 221 346 Z"
        fill="#000000"
        opacity="0.08"
      />
    </>
  ) : null;

  return (
    <Box
      component="span"
      className={tierBadgeClassNames.root}
      sx={tierBadgeStyles.root(size)}
      role={isProgressIndicator ? 'progressbar' : accessibleLabel ? 'img' : undefined}
      aria-label={accessibleLabel}
      aria-hidden={accessibleLabel ? undefined : true}
      aria-valuemin={isProgressIndicator ? 0 : undefined}
      aria-valuemax={isProgressIndicator ? 100 : undefined}
      aria-valuenow={normalizedProgress}
      data-tier-badge-variant={variant}
      data-tier-badge-progress={normalizedProgress}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 360 435"
        focusable="false"
        aria-hidden="true"
      >
        {colors.gradient ? (
          <defs>
            <linearGradient id={gradientId} x1="24%" y1="0%" x2="78%" y2="100%">
              <stop offset="0%" stopColor={colors.gradient.highlight} />
              <stop offset="42%" stopColor={colors.gradient.mid} />
              <stop offset="72%" stopColor={colors.gradient.shadow} />
              <stop offset="100%" stopColor={colors.gradient.mid} />
            </linearGradient>
            <clipPath id={clipPathId}>
              <use href={TIER_BADGE_SHIELD_HREF} />
            </clipPath>
            {isProgressIndicator ? (
              <clipPath id={progressClipPathId}>
                <rect
                  data-tier-badge-progress-clip
                  x="0"
                  y="0"
                  width={progressWidth}
                  height="435"
                />
              </clipPath>
            ) : null}
          </defs>
        ) : null}
        <use
          href={TIER_BADGE_SHIELD_HREF}
          fill={isProgressIndicator ? tierBadgeColors.unavailable.fill : fill}
          stroke={isProgressIndicator ? 'none' : colors.border}
        />
        {colors.gradient && isProgressIndicator && normalizedProgress > 0 ? (
          <g clipPath={`url(#${clipPathId})`}>
            <g clipPath={`url(#${progressClipPathId})`}>
              <rect
                data-tier-badge-progress-fill
                x="0"
                y="0"
                width="360"
                height="435"
                fill={fill}
              />
              {shine}
            </g>
          </g>
        ) : null}
        {colors.gradient && !isProgressIndicator ? (
          <g clipPath={`url(#${clipPathId})`}>
            {shine}
          </g>
        ) : null}
        {isProgressIndicator ? (
          <use
            href={TIER_BADGE_SHIELD_HREF}
            fill="none"
            stroke={colors.border}
          />
        ) : null}
      </svg>
    </Box>
  );
}

function normalizeProgress(progress: number | undefined): number | undefined {
  if (progress === undefined) {
    return undefined;
  }

  if (!Number.isFinite(progress)) {
    return 0;
  }

  return Math.min(100, Math.max(0, progress));
}

function useStableSvgId(prefix: string): string {
  return `${prefix}-${useId().replace(/:/g, '')}`;
}
